import './LinkModal.scss'
import { useEffect, useState } from 'react'
import Modal from '../../components/Modal'
import AccountIcon from '../../components/AccountIcon'
import { addStatementLink, linkBankTransactions, listLinkCandidates } from '../../ApiInterface'
import { formatCurrency, formatDate, isBankAccount, isFromBank } from '../../Helpers'
import { applyImpact, formatImpact } from '../../Impact'

interface Props {
    bankTransaction: BankTransaction
    accounts: Account[]
    budgets: Budget[]
    // Opération à présélectionner (suggestion « lien possible ? »)
    preselectedId?: number | null
    onCancel: () => void
    onDone: () => void
}

type Choice = { kind: 'merge', other: BankTransaction } | { kind: 'add', account: Account }

const MIN_SEARCH_DELAY = 250

const LinkModal = ({ bankTransaction, accounts, budgets, preselectedId, onCancel, onDone }: Props) => {
    const [query, setQuery] = useState('')
    const [accountFilter, setAccountFilter] = useState('')
    const [candidates, setCandidates] = useState(null as LinkCandidates | null)
    const [choice, setChoice] = useState(null as Choice | null)
    const [impact, setImpact] = useState(null as Impact | null)
    const [budgetId, setBudgetId] = useState(undefined as number | null | undefined)
    const [error, setError] = useState('')

    const accountById = (id: number) => accounts.find((account) => account.id === id)
    const budgetName = (id: number | null) => (id && budgets.find((budget) => budget.id === id)?.name) || 'à catégoriser'

    useEffect(() => {
        const timeout = setTimeout(() => {
            listLinkCandidates(bankTransaction.id, query, accountFilter ? Number(accountFilter) : undefined).then((candidates) => {
                setCandidates(candidates)
                setChoice((choice) => {
                    if(choice) {
                        return choice
                    }
                    const preselected = [...candidates.suggestions, ...candidates.others].find((other) => other.id === preselectedId)
                    return preselected ? { kind: 'merge', other: preselected } : null
                })
            })
        }, query ? MIN_SEARCH_DELAY : 0)
        return () => clearTimeout(timeout)
    }, [bankTransaction.id, query, accountFilter, preselectedId])

    // Choix par défaut de l'aperçu à chaque nouvelle opération choisie.
    useEffect(() => {
        setImpact(null)
        setBudgetId(undefined)
        setError('')
    }, [choice])

    // Comptes où ajouter l'opération sans fusion : ni de banque, ni déjà un de ses relevés.
    const placedAccountIds = [bankTransaction.account_id, ...bankTransaction.statement_links.map((link) => link.account_id)]
    const addableAccounts = accounts.filter((account) => !isBankAccount(account) && !account.tricount && !placedAccountIds.includes(account.id))
    const otherAccounts = accounts.filter((account) => account.id !== bankTransaction.account_id)

    const confirm = () => {
        if(!choice) {
            return
        }
        const request = choice.kind === 'add'
            ? addStatementLink(bankTransaction.id, choice.account.id)
            : linkBankTransactions(bankTransaction.id, choice.other.id, {
                ...(impact !== null ? { impact } : {}),
                ...(budgetId !== undefined ? { budget_id: budgetId } : {})
            })
        request.then(onDone).catch((e) => setError(e.response?.data?.message || 'Le lien a échoué'))
    }

    const candidateRow = (other: BankTransaction, match?: string) => {
        const account = accountById(other.account_id)
        const selected = choice?.kind === 'merge' && choice.other.id === other.id
        return <div key={other.id} className={`candidate${selected ? ' selected' : ''}`} onClick={() => setChoice({ kind: 'merge', other })}>
            { account && <AccountIcon account={account} size={30}/> }
            <div className="t">
                <b>{ other.user_label || other.label }</b>
                <small>{ formatDate(other.at) } · { account?.name } · { budgetName(other.budget_id) } · { formatImpact(other.impact) }</small>
            </div>
            { match && <span className={`match ${other.amount_cents === bankTransaction.amount_cents ? 'exact' : 'near'}`}>{ match }</span> }
            <div className={`m${other.amount_cents < 0 ? ' credit' : ''}`}>{ formatCurrency(other.amount_cents) }</div>
        </div>
    }

    const matchLabel = (other: BankTransaction) => {
        const days = Math.round((new Date(other.at).getTime() - new Date(bankTransaction.at).getTime()) / 86_400_000)
        const daysLabel = days === 0 ? 'même jour' : `${days > 0 ? '+' : ''}${days} jour${Math.abs(days) > 1 ? 's' : ''}`
        const gap = other.amount_cents - bankTransaction.amount_cents
        return `${gap === 0 ? 'même montant' : `${gap > 0 ? '+' : ''}${formatCurrency(gap)}`} · ${daysLabel}`
    }

    const preview = () => {
        if(!choice) {
            return null
        }
        if(choice.kind === 'add') {
            return <div className="preview">
                L'opération apparaîtra aussi dans le relevé <b>{ choice.account.name }</b>, avec son budget et son %. Rien n'est supprimé.
            </div>
        }
        const other = choice.other
        const otherFromBank = isFromBank(other, accounts)
        const [kept, absorbed] = otherFromBank ? [other, bankTransaction] : [bankTransaction, other]
        const keptAccount = accountById(kept.account_id), absorbedAccount = accountById(absorbed.account_id)
        const absorbedTricount = (absorbed.external_id || '').startsWith('tricount:')
        const userLabel = kept.user_label || absorbed.user_label || (absorbed.label !== kept.label ? absorbed.label : null)
        const defaultBudgetId = kept.budget_id || absorbed.budget_id
        const chosenBudgetId = budgetId === undefined ? defaultBudgetId : budgetId
        const chosenImpact = absorbedTricount ? absorbed.impact : (impact ?? absorbed.impact)
        const counted = Math.round(applyImpact(kept.amount_cents, chosenImpact))
        const before = Math.round(applyImpact(kept.amount_cents, kept.impact) + applyImpact(absorbed.amount_cents, absorbed.impact))

        return <>
            <table className="merge">
                <thead><tr><th></th><th>{ keptAccount?.name } (gardée)</th><th>{ absorbedAccount?.name }</th></tr></thead>
                <tbody>
                    <tr><td className="k">Date</td><td className="win">{ formatDate(kept.at) }</td><td className="lose">{ formatDate(absorbed.at) }</td></tr>
                    <tr><td className="k">Montant</td><td className="win">{ formatCurrency(kept.amount_cents) }</td><td className="lose">{ formatCurrency(absorbed.amount_cents) }</td></tr>
                    <tr><td className="k">Libellé</td><td className="win" colSpan={2}>{ userLabel || kept.label }</td></tr>
                    <tr>
                        <td className="k">Budget</td>
                        { kept.budget_id && absorbed.budget_id && kept.budget_id !== absorbed.budget_id
                            ? [kept, absorbed].map((t) => <td key={t.id}><label>
                                <input type="radio" name="budget" checked={chosenBudgetId === t.budget_id} onChange={() => setBudgetId(t.budget_id)}/>
                                { budgetName(t.budget_id) }
                            </label></td>)
                            : <td className="win" colSpan={2}>{ budgetName(defaultBudgetId) }</td> }
                    </tr>
                    <tr>
                        <td className="k">Ma part</td>
                        { !absorbedTricount && kept.impact !== absorbed.impact
                            ? [kept, absorbed].map((t) => <td key={t.id}><label>
                                <input type="radio" name="impact" checked={chosenImpact === t.impact} onChange={() => setImpact(t.impact)}/>
                                { formatImpact(t.impact) }
                            </label></td>)
                            : <td className="win" colSpan={2}>{ formatImpact(chosenImpact) }{ absorbedTricount && ' (part du Tricount, mise à jour par la synchro)' }</td> }
                    </tr>
                </tbody>
            </table>
            { kept.amount_cents !== absorbed.amount_cents && <div className="preview warning">
                Les montants diffèrent de { formatCurrency(Math.abs(kept.amount_cents - absorbed.amount_cents)) } : le montant de { keptAccount?.name } est gardé.
            </div> }
            <div className="preview">
                « { absorbed.user_label || absorbed.label } » est remplacée par l'opération { keptAccount?.name }, qui apparaît dans les deux relevés :
                <b> { formatCurrency(counted) }</b> dans les budgets au lieu de { formatCurrency(before) }.
            </div>
        </>
    }

    const account = accountById(bankTransaction.account_id)

    return <Modal onCancel={onCancel}>
        <div className="link-modal">
            <h3>Lier à une autre opération</h3>
            <div className="sub">Les deux deviennent une seule opération, présente dans les deux relevés</div>
            <div className="from">
                { account && <AccountIcon account={account} size={34}/> }
                <div className="t">
                    <b>{ bankTransaction.user_label || bankTransaction.label }</b>
                    <small>{ formatDate(bankTransaction.at) } · { account?.name } · { budgetName(bankTransaction.budget_id) } · { formatImpact(bankTransaction.impact) }</small>
                </div>
                <div className="m">{ formatCurrency(bankTransaction.amount_cents) }</div>
            </div>
            <div className="search">
                <input className="tl-input" placeholder="Rechercher un libellé, un montant…" value={query} autoFocus
                    onChange={(e) => setQuery(e.target.value)}/>
                <select className="tl-select" value={accountFilter} onChange={(e) => setAccountFilter(e.target.value)}>
                    <option value="">Tous les comptes</option>
                    { otherAccounts.map((account) => <option key={account.id} value={account.id}>{ account.name }</option>) }
                </select>
            </div>
            <div className="candidates">
                { candidates && candidates.suggestions.length > 0 && <>
                    <h4>Suggestions</h4>
                    { candidates.suggestions.map((other) => candidateRow(other, matchLabel(other))) }
                </> }
                { candidates && candidates.others.length > 0 && <>
                    <h4>{ query ? 'Résultats' : 'Autres opérations proches' }</h4>
                    { candidates.others.map((other) => candidateRow(other)) }
                </> }
                { candidates && candidates.suggestions.length + candidates.others.length === 0 && <div className="empty">Aucune opération à lier</div> }
                { addableAccounts.length > 0 && !accountFilter && !query && <>
                    <h4>Ajouter sans fusionner</h4>
                    { addableAccounts.map((account) => <div key={account.id}
                        className={`candidate${choice?.kind === 'add' && choice.account.id === account.id ? ' selected' : ''}`}
                        onClick={() => setChoice({ kind: 'add', account })}>
                        <AccountIcon account={account} size={30}/>
                        <div className="t"><b>Ajouter au relevé { account.name }</b><small>sans opération existante à fusionner</small></div>
                    </div>) }
                </> }
            </div>
            { preview() }
            { error && <div className="preview warning">{ error }</div> }
            <div className="buttons">
                <button className="tl-btn tl-btn-ghost" onClick={onCancel}>Annuler</button>
                <button className="tl-btn tl-btn-primary" disabled={!choice} onClick={confirm}>
                    { choice?.kind === 'add' ? 'Ajouter' : 'Fusionner' }
                </button>
            </div>
        </div>
    </Modal>
}

export default LinkModal
